import { useMemo, useState } from "react";

import { FileText, Search } from "lucide-react";
import { Link } from "react-router-dom";
import Datatable from "@components/datatable";
import StatusChip from "@components/ui-components/status-chip";
import Select from "@components/ui-components/select";
import useBrandRouteGuard from "../use-brand-route-guard";
import useBrandPublishers from "./use-brand-publishers";
import { formatCurrency } from "@src/utility/helpers/utils";
import { KYC_LABEL, KYC_TONE, PAYOUT_LABEL, PAYOUT_TONE } from "@src/utility/constants/panel/common";

const KYC_FILTER_OPTIONS = [
    { value: "all", label: "All KYC statuses" },
    { value: "not_started", label: "Not started" },
    { value: "verifying", label: "Verifying" },
    { value: "verified", label: "Verified" },
    { value: "failed", label: "Failed" },
    { value: "escalated", label: "Escalated" }
];

const PAYOUT_FILTER_OPTIONS = [
    { value: "all", label: "All payout statuses" },
    { value: "pending", label: "Pending" },
    { value: "eligible", label: "Eligible" },
    { value: "paid", label: "Paid" },
    { value: "held", label: "Held" }
];

const BrandPublishers = () => {
    const { brandId } = useBrandRouteGuard();
    const {
        loading, currency, publishers, pagination, search, setSearch,
        handlePageChange, handlePerRowsChange, totalPending, verifiedCount, totalPublishers
    } = useBrandPublishers(brandId);
    const [kycFilter, setKycFilter] = useState("all");
    const [payoutFilter, setPayoutFilter] = useState("all");

    // Search is server-side; KYC/payout filters only narrow the current page
    // (no server params confirmed for those yet).
    const filtered = useMemo(() => publishers.filter((p) => {
        if (kycFilter !== "all" && p.kyc_status !== kycFilter) return false;
        if (payoutFilter !== "all" && p.payout_status !== payoutFilter) return false;
        return true;
    }), [publishers, kycFilter, payoutFilter]);

    const columns = [
        { Header: "Publisher", accessor: "holder_name", Cell: ({ value }) => <span className="font-medium text-gray-900">{value}</span> },
        { Header: "Email", accessor: "email", Cell: ({ value }) => <span className="text-gray-700">{value}</span> },
        { Header: "Invoice", accessor: "invoice_number", Cell: ({ value }) => <span className="font-mono text-xs text-gray-700">{value || "—"}</span> },
        { Header: "Amount", accessor: "pending_amount", Cell: ({ value }) => <span className="text-gray-700">{formatCurrency(value, currency)}</span> },
        { Header: "KYC", accessor: "kyc_status", Cell: ({ value }) => <StatusChip tone={KYC_TONE[value] || "neutral"} label={KYC_LABEL[value] || value} /> },
        { Header: "Payout", accessor: "payout_status", Cell: ({ value }) => <StatusChip tone={PAYOUT_TONE[value] || "neutral"} label={PAYOUT_LABEL[value] || value} /> },
        {
            Header  : "",
            accessor: "_id",
            // Row is handed over in route state so the invoices header can show
            // the publisher's name/KYC before its own request resolves.
            Cell    : ({ row }) => (
                <div className="flex items-center justify-end">
                    <Link
                        to={`/brand/${brandId}/publishers/${row._id}/invoices`}
                        state={{ publisher: row }}
                        className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50">
                        <FileText className="h-3.5 w-3.5" />
                        View invoices
                    </Link>
                </div>
            )
        }
    ];

    return (
        <div className="p-6">
            <h1 className="text-2xl font-semibold tracking-tight">Publishers</h1>

            <div className="mt-6 grid gap-3 md:grid-cols-3">
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <p className="text-sm text-muted">Total publishers</p>
                    <p className="mt-1 text-2xl font-semibold text-gray-900">{totalPublishers}</p>
                </div>
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <p className="text-sm text-muted">KYC verified</p>
                    <p className="mt-1 text-2xl font-semibold text-gray-900">
                        {verifiedCount}
                        <span className="ml-1 text-sm font-normal text-muted">/ {totalPublishers}</span>
                    </p>
                </div>
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <p className="text-sm text-muted">Total pending</p>
                    <p className="mt-1 text-2xl font-semibold text-gray-900">{formatCurrency(totalPending, currency)}</p>
                </div>
            </div>

            <div className="mt-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="grid gap-3 md:grid-cols-3">
                    <div className="relative">
                        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search name or email…"
                            className="w-full rounded-lg border border-gray-300 py-2.5 pl-9 pr-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" />
                    </div>
                    <Select
                        id="kyc-filter"
                        value={kycFilter}
                        options={KYC_FILTER_OPTIONS}
                        onChange={setKycFilter} />
                    <Select
                        id="payout-filter"
                        value={payoutFilter}
                        options={PAYOUT_FILTER_OPTIONS}
                        onChange={setPayoutFilter} />
                </div>
            </div>

            <div className="mt-4 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                <Datatable
                    columns={columns}
                    data={filtered}
                    loading={loading}
                    pagination={{ count: pagination.total, page: pagination.page, limit: pagination.limit }}
                    handlePageChange={handlePageChange}
                    handlePerRowsChange={handlePerRowsChange}
                    customNoData={<span className="text-sm text-muted">No publishers match those filters.</span>} />
            </div>
        </div>
    );
};

export default BrandPublishers;
